import type { RuleGroupType } from "react-querybuilder";

export interface StoredFilter {
  id: number;
  name: string;
  rules: RuleGroupType;
}

const ACTIVE_KEY = (ct: number) => `qf_active_${ct}`;
const DEFAULT_KEY = (ct: number) => `qf_default_${ct}`;
const QUERY_KEY = (ct: number) => `query_filter_rules_${ct}`;
const FIELDS_CACHE_KEY = `query_initial_fields_cache:${window.location.pathname}`;

function readJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function hasRules(rules: RuleGroupType | undefined): boolean {
  return !!rules?.rules && rules.rules.length > 0;
}

// --- Active filter (last user-selected) ---

export function getActiveFilter(ct: number): StoredFilter | null {
  return readJSON<StoredFilter>(ACTIVE_KEY(ct));
}

export function setActiveFilter(ct: number, filter: StoredFilter) {
  localStorage.setItem(ACTIVE_KEY(ct), JSON.stringify(filter));
}

export function clearActiveFilter(ct: number) {
  localStorage.removeItem(ACTIVE_KEY(ct));
}

// --- Default (starred) filter ---

export function getDefaultFilter(ct: number): StoredFilter | null {
  return readJSON<StoredFilter>(DEFAULT_KEY(ct));
}

export function setDefaultFilter(ct: number, filter: StoredFilter) {
  localStorage.setItem(DEFAULT_KEY(ct), JSON.stringify(filter));
}

export function clearDefaultFilter(ct: number) {
  localStorage.removeItem(DEFAULT_KEY(ct));
}

export function forgetFilter(ct: number, id: number): boolean {
  if (getDefaultFilter(ct)?.id === id) clearDefaultFilter(ct);
  const wasActive = getActiveFilter(ct)?.id === id;
  if (wasActive) clearActiveFilter(ct);
  return wasActive;
}

// --- Resolved initial state (single priority logic) ---

export function getInitialFilter(ct: number): StoredFilter | null {
  if (ct <= 0) return null;
  const def = getDefaultFilter(ct);
  if (def && hasRules(def.rules)) return def;
  const active = getActiveFilter(ct);
  if (active && hasRules(active.rules)) return active;
  return null;
}

/**
 * Returns initial filterRules string for LiveTable.
 * Priority: default filter > active filter > saved query builder state > ""
 */
export function getInitialFilterRules(): string {
  try {
    const cache = readJSON<{ content_type?: number }>(FIELDS_CACHE_KEY);
    const ct = cache?.content_type;
    if (!ct) return "";
    const initial = getInitialFilter(ct);
    if (initial) return JSON.stringify(initial.rules);
    const saved = readJSON<RuleGroupType>(QUERY_KEY(ct));
    if (saved && hasRules(saved)) return JSON.stringify(saved);
  } catch { /* no cache */ }
  return "";
}
